// The installable app and Web Push in the browser: registers /sw.js, follows taps on notifications (the worker asks
// an open window to navigate), keeps Chrome's install prompt for an "Install app" button, and subscribes this device
// for push. The server side lives in apps/server/src/push.
import type { PushStatusView } from "@plannello/shared";
import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";

type Navigate = (href: string) => void;

/** Registers the service worker once the page has loaded; taps on notifications navigate inside the app. */
export function startServiceWorker(navigate: Navigate): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
    const d = e.data as { type?: string; url?: string } | null;
    if (d?.type === "plannello:navigate" && typeof d.url === "string" && d.url.startsWith("/")) navigate(d.url);
  });
  const register = () => void navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {});
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    installPrompt = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installPrompt = null;
    notify();
  });
}

// ---------------------------------------------------------------------------------------------------------
// Install prompt
// ---------------------------------------------------------------------------------------------------------

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let installPrompt: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** True while the browser offers to install the app (Chrome, Edge, Samsung Internet). */
export function useCanInstall(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => installPrompt !== null,
    () => false,
  );
}

export async function promptInstall(): Promise<boolean> {
  const p = installPrompt;
  if (!p) return false;
  await p.prompt();
  const { outcome } = await p.userChoice;
  installPrompt = null;
  notify();
  return outcome === "accepted";
}

/** Running as the installed app (standalone window), not in a browser tab. */
export function isInstalled(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

// ---------------------------------------------------------------------------------------------------------
// Push on this device
// ---------------------------------------------------------------------------------------------------------

/**
 * What this browser can do: "ok"; "install-first" (iPhone and iPad only allow push for apps added to the home
 * screen); "unsupported".
 */
export function pushSupport(): "ok" | "install-first" | "unsupported" {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  if (ios && !isInstalled()) return "install-first";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  return "ok";
}

const keyBytes = (base64url: string) => {
  const s = atob(base64url.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return existing;
  await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

/** This browser's current subscription (null when push is off here). */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== "ok") return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export class PushPermissionDenied extends Error {}

/** Asks for permission, subscribes this browser with the server's key and registers it. */
export async function enablePush(publicKey: string): Promise<PushStatusView> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new PushPermissionDenied(permission);
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with another server key (a reinstalled server) cannot receive our messages.
  const key = sub?.options.applicationServerKey;
  if (sub && key && !sameBytes(new Uint8Array(key), keyBytes(publicKey))) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  return api.put<PushStatusView>("/api/v1/me/push/subscription", sub.toJSON());
}

/** Unsubscribes this browser and forgets it on the server. */
export async function disablePush(): Promise<PushStatusView | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  return api.delete<PushStatusView>("/api/v1/me/push/subscription", { endpoint });
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}
